import { useCallback } from 'react';
import { usePortalAuthStore } from '../stores/portalAuthStore';
import { useLoginModalStore } from '../stores/loginModalStore';

/**
 * Hook that provides a gate function for auth-requiring actions.
 *
 * Usage:
 *   const requireAuth = useAuthGate();
 *
 *   const handleAddToCart = () => {
 *     if (!requireAuth()) return;  // Opens login modal if not authenticated
 *     // ... proceed with add to cart
 *   };
 *
 * Returns a function that:
 * - Returns true if the user is authenticated (action can proceed)
 * - Returns false and opens the login modal if not authenticated
 */
export function useAuthGate(): (message?: string) => boolean {
	const openLoginModal = useLoginModalStore((s) => s.open);

	return useCallback(
		(message?: string) => {
			const { isAuthenticated, token } = usePortalAuthStore.getState();

			if (isAuthenticated && token != null) {
				return true;
			}

			openLoginModal(message);
			return false;
		},
		[openLoginModal],
	);
}
